import { Component, EventEmitter, Input, Output } from '@angular/core';
import { User, UserManagementColumn, UsersBrands, SortState } from '../types';
import { AgGridAngular } from 'ag-grid-angular';
import { ClientSideRowModelModule, GridOptions, RowClickedEvent, themeQuartz, type ColDef, SortChangedEvent } from "ag-grid-community";
import { AgTooltipCellComponent } from '../../../shared/components/ag-tooltip-cell/ag-tooltip-cell.component';
import { getManagementDateFormat } from '../../../utils/time';

@Component({
  selector: 'app-users-table',
  imports: [AgGridAngular],
  templateUrl: './users-table.component.html',
  styleUrl: './users-table.component.scss'
})
export class UsersTableComponent {
  @Input() rows: User[] = [];
  @Output() rowClicked = new EventEmitter<User>();
  @Output() sortChanged = new EventEmitter<SortState>();

  colDefs: ColDef[] = this.getColDef();
  theme = themeQuartz.withParams({
    spacing: 10,
    headerBackgroundColor: 'rgba(217, 217, 217, 0.3)',
    rowHoverColor: 'rgba(233, 244, 245, 0.85)',
    fontSize: '14px',
    headerFontSize: '16px',
    fontFamily: 'Poppins',
    headerFontFamily: 'Poppins',
    headerFontWeight: 600,
    headerTextColor: '#717172;',
    cellTextColor: '#717172;',
    headerHeight: 64,
  });
  modules = [ClientSideRowModelModule];
  gridOptions: GridOptions = {
    suppressMovableColumns: true,
    domLayout: 'autoHeight',
    rowHeight: undefined,
    defaultColDef: {
      resizable: false,
      autoHeight: true,
      sortable: true,
      wrapText: true,
      flex: 1
    },
  };

  public onRowClicked = (event: RowClickedEvent): void => {
    this.rowClicked.emit(event.data as User);
  };

  public onSortChanged(event: SortChangedEvent): void {
    // ag-grid fires sortChanged for non-user reasons too (initialization, row-data updates,
    // flex/resize). Only a real header click should reload the table.
    if (event.source !== 'uiColumnSorted') {
      return;
    }
    const sortModel = event.columns?.[0];
    this.sortChanged.emit({
      sortBy: sortModel?.getColId() || '',
      sortDir: (sortModel?.getSort() as 'asc' | 'desc') || 'desc',
    });
  }

  private getColDef(): ColDef[] {
    return [
      {
        field: UserManagementColumn.EMAIL,
        cellRenderer: AgTooltipCellComponent,
        flex: 1.5,
      },
      {
        field: UserManagementColumn.FIRST_NAME,
        valueGetter: (params: any) => {
          return params.data[UserManagementColumn.FIRST_NAME] || '';
        }
      },
      {
        field: UserManagementColumn.LAST_NAME,
        valueGetter: (params: any) => {
          return params.data[UserManagementColumn.LAST_NAME] || '';
        }
      },
      {
        field: UserManagementColumn.ROLE,
        sortable: false,
      },
      {
        field: UserManagementColumn.CONNECTED_BRANDS,
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          return params.data[UserManagementColumn.CONNECTED_BRANDS]
            .map((item: UsersBrands) => item?.name)
            .filter(Boolean)
            .join(', ');
        },
        sortable: false,
      },
      {
        field: UserManagementColumn.ACCOUNT,
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          return params.data[UserManagementColumn.ACCOUNT]?.name
        },
        sortable: false,
      },
      {
        field: UserManagementColumn.CREATED_AT,
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          return getManagementDateFormat(params.data[UserManagementColumn.CREATED_AT])
        }
      },
      {
        field: UserManagementColumn.LAST_LOGIN,
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          return params.data[UserManagementColumn.LAST_LOGIN] ? getManagementDateFormat(params.data[UserManagementColumn.LAST_LOGIN]) : ''
        },
        sortable: false,
      },
    ];
  }
}
